import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

function Header() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hikeOpen, setHikeOpen] = useState(false);
  const [travelOpen, setTravelOpen] = useState(false);
  const travelRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setHikeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (travelRef.current && !travelRef.current.contains(e.target)) {
        setTravelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="bg-green-700 text-white shadow-lg">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo192.png" alt="HikingBuddy" className="h-12 w-12" />
            <span className="text-2xl font-bold">HikingBuddy</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {user ? (
              <>
                {/* Hike dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setHikeOpen(o => !o)}
                    className="flex items-center gap-1 hover:text-green-200 transition-colors focus:outline-none"
                  >
                    Hike
                    <svg className={`w-4 h-4 transition-transform ${hikeOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {hikeOpen && (
                    <div className="absolute left-0 mt-2 w-44 bg-green-800 rounded shadow-lg py-1" style={{zIndex: 200}}>
                      <Link
                        to="/hike/setup"
                        onClick={() => setHikeOpen(false)}
                        className="block px-4 py-2 hover:bg-green-700 transition-colors"
                      >
                        Setup Hike
                      </Link>
                      <Link
                        to="/hike/update"
                        onClick={() => setHikeOpen(false)}
                        className="block px-4 py-2 hover:bg-green-700 transition-colors"
                      >
                        Update Hike
                      </Link>
                      <Link
                        to="/hike/report"
                        onClick={() => setHikeOpen(false)}
                        className="block px-4 py-2 hover:bg-green-700 transition-colors"
                      >
                        View Report
                      </Link>
                    </div>
                  )}
                </div>

                {/* Travel dropdown */}
                <div className="relative" ref={travelRef}>
                  <button
                    onClick={() => setTravelOpen(o => !o)}
                    className="flex items-center gap-1 hover:text-green-200 transition-colors focus:outline-none"
                  >
                    Travel
                    <svg className={`w-4 h-4 transition-transform ${travelOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {travelOpen && (
                    <div className="absolute left-0 mt-2 w-36 bg-green-800 rounded shadow-lg py-1" style={{zIndex: 200}}>
                      <Link
                        to="/travel"
                        onClick={() => setTravelOpen(false)}
                        className="block px-4 py-2 hover:bg-green-700 transition-colors"
                      >
                        My Trips
                      </Link>
                      <a
                        href="/guide.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setTravelOpen(false)}
                        className="block px-4 py-2 hover:bg-green-700 transition-colors"
                      >
                        Guide ↗
                      </a>
                    </div>
                  )}
                </div>

                <div className="border-l border-green-500 h-6 mx-2"></div>
                <span className="text-green-200">{user.username}</span>
                <button
                  onClick={handleLogout}
                  className="bg-green-600 hover:bg-green-500 px-4 py-2 rounded transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="hover:text-green-200 transition-colors">Login</Link>
                <Link to="/register" className="bg-green-600 hover:bg-green-500 px-4 py-2 rounded transition-colors">Register</Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden focus:outline-none"
            onClick={() => {
              const menu = document.getElementById('mobile-menu');
              menu.classList.toggle('hidden');
            }}
          >
            <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <div id="mobile-menu" className="hidden md:hidden mt-4 space-y-2">
          {user ? (
            <>
              <div className="py-1 text-green-300 text-xs uppercase tracking-widest">Hike</div>
              <Link to="/hike/setup" className="block py-2 pl-3 hover:text-green-200 transition-colors">Setup Hike</Link>
              <Link to="/hike/update" className="block py-2 pl-3 hover:text-green-200 transition-colors">Update Hike</Link>
              <Link to="/hike/report" className="block py-2 pl-3 hover:text-green-200 transition-colors">View Report</Link>
              <div className="border-t border-green-600 my-2"></div>
              <div className="py-1 text-green-300 text-xs uppercase tracking-widest">Travel</div>
              <Link to="/travel" className="block py-2 pl-3 hover:text-green-200 transition-colors">My Trips</Link>
              <a href="/guide.html" className="block py-2 pl-3 hover:text-green-200 transition-colors">Guide</a>
              <div className="border-t border-green-600 my-2"></div>
              <div className="py-2 text-green-200">{user.username}</div>
              <button onClick={handleLogout} className="w-full text-left py-2 hover:text-green-200 transition-colors">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block py-2 hover:text-green-200 transition-colors">Login</Link>
              <Link to="/register" className="block py-2 hover:text-green-200 transition-colors">Register</Link>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

export default Header;